import { useCallback, useMemo, useRef, useState } from 'react';

interface HeadersObject {
  [key: string]: string;
}

const headersToObject = (headers?: HeadersInit): HeadersObject => {
  if (!headers) {
    return {};
  }

  if (headers instanceof globalThis.Headers) {
    const obj: HeadersObject = {};
    headers.forEach((value, key) => {
      obj[key] = value;
    });
    return obj;
  }

  if (Array.isArray(headers)) {
    return headers.reduce<HeadersObject>((acc, [key, value]) => {
      acc[key] = value;
      return acc;
    }, {});
  }

  return { ...headers };
};

export interface UseGatewaySessionAuthConfig {
  sessionPath?: string;
  loginPath?: string;
  fetchImpl?: typeof fetch;
  requestInit?: RequestInit;
  authenticatedFetchHeaders?: Record<string, string>;
  redirectToLogin?: (loginUrl: string) => void;
  initialSessionReady?: boolean;
  onSessionInvalid?: (response: Response) => void;
  onSessionValidated?: (response: Response) => void;
}

export interface UseGatewaySessionAuthArgs {
  authBase: string;
  config?: UseGatewaySessionAuthConfig;
}

export const useGatewaySessionAuth = ({ authBase, config }: UseGatewaySessionAuthArgs) => {
  const {
    sessionPath = '/auth/session',
    loginPath = '/auth/login',
    fetchImpl = fetch,
    requestInit,
    authenticatedFetchHeaders,
    redirectToLogin: redirectOverride,
    initialSessionReady = false,
    onSessionInvalid,
    onSessionValidated,
  } = config ?? {};

  const normalizedSessionPath = sessionPath.startsWith('/') ? sessionPath : `/${sessionPath}`;
  const normalizedLoginPath = loginPath.startsWith('/') ? loginPath : `/${loginPath}`;

  const [sessionReady, setSessionReady] = useState(initialSessionReady);
  const ensureSessionPromiseRef = useRef<Promise<boolean> | null>(null);
  const redirectInFlightRef = useRef(false);

  const ensureSessionRequestInit = useMemo(() => {
    const baseHeaders = {
      'Cache-Control': 'no-store',
      ...headersToObject(requestInit?.headers),
    };

    return {
      method: 'GET',
      credentials: requestInit?.credentials ?? 'include',
      ...requestInit,
      headers: baseHeaders,
    } satisfies RequestInit;
  }, [requestInit]);

  const buildLoginUrl = useCallback(() => {
    const returnTo =
      typeof window !== 'undefined'
        ? encodeURIComponent(`${window.location.pathname}${window.location.search}`)
        : '';
    return `${authBase}${normalizedLoginPath}?return_to=${returnTo}`;
  }, [authBase, normalizedLoginPath]);

  const redirectToLogin = useCallback(() => {
    const loginUrl = buildLoginUrl();
    if (redirectOverride) {
      redirectOverride(loginUrl);
      return;
    }

    if (typeof window !== 'undefined') {
      window.location.href = loginUrl;
    }
  }, [buildLoginUrl, redirectOverride]);

  const ensureSession = useCallback(async () => {
    if (redirectInFlightRef.current) {
      return false;
    }

    if (sessionReady) {
      return true;
    }

    if (ensureSessionPromiseRef.current) {
      return ensureSessionPromiseRef.current;
    }

    const ensurePromise = (async () => {
      try {
        const response = await fetchImpl(
          `${authBase}${normalizedSessionPath}`,
          ensureSessionRequestInit
        );

        if (response.ok) {
          setSessionReady(true);
          redirectInFlightRef.current = false;
          onSessionValidated?.(response);
          return true;
        }

        setSessionReady(false);
        onSessionInvalid?.(response);

        if (response.status === 401) {
          redirectInFlightRef.current = true;
          redirectToLogin();
          console.warn('Redirecting to login');
          return false;
        }

        console.warn(`Unable to validate session: ${response.status}`);
        return false;
      } catch (error) {
        console.error('Failed to validate session', error);
        setSessionReady(false);
        redirectInFlightRef.current = false;
        throw error;
      } finally {
        ensureSessionPromiseRef.current = null;
      }
    })();

    ensureSessionPromiseRef.current = ensurePromise;
    return ensurePromise;
  }, [
    authBase,
    ensureSessionRequestInit,
    fetchImpl,
    normalizedSessionPath,
    onSessionInvalid,
    onSessionValidated,
    redirectToLogin,
    sessionReady,
  ]);

  const authenticatedFetch = useCallback(
    async (url: string, options: RequestInit = {}) => {
      const sessionValid = await ensureSession();
      if (!sessionValid) {
        throw new Error('Unable to validate gateway session');
      }

      const requestHeaders: HeadersObject = {
        'X-Requested-With': 'XMLHttpRequest',
        ...(authenticatedFetchHeaders ?? {}),
        ...headersToObject(options.headers),
      };

      return fetchImpl(url, {
        ...options,
        credentials: options.credentials ?? 'include',
        headers: requestHeaders,
      });
    },
    [authenticatedFetchHeaders, ensureSession, fetchImpl]
  );

  const setSessionReadyState = useCallback((ready: boolean) => {
    setSessionReady(ready);
    if (ready) {
      redirectInFlightRef.current = false;
    }
  }, []);

  return {
    sessionReady,
    ensureSession,
    authenticatedFetch,
    redirectToLogin,
    setSessionReady: setSessionReadyState,
  };
};
